import { createFileRoute } from "@tanstack/react-router";
import { AgentTable } from "../../queues/-component/agent-table";
import { useEffect, useMemo, useState } from "react";
import { useAppForm } from "@/components/form/form-context";
import {
  queueGroupAgentsQueryOptions,
  queueGroupQueuesQueryOptions,
  updateQueueGroupAttendantsFn,
} from "@/server/queues";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CreateQueueAttendant, QueueAttendant } from "@/lib/mma/types.gen";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { SelectValue } from "@radix-ui/react-select";
import { ColumnDef } from "@tanstack/react-table";
import { AssociationStatus } from "../-components/associationStatus";
import { toast } from "sonner";
import { CircleCheckBig } from "lucide-react";

export const Route = createFileRoute("/_dashboard/queue-groups/$queueGroupId/agents")({
  component: RouteComponent,
  loader: async ({ params }) => {
    return { crumb: "Agents", params };
  },
});

const COLUMN_WIDTHS = {
  active: "110px",
  fname: "180px",
  lname: "180px",
  username: "180px",
  noAssociatedQueues: "130px",
  skill: "140px",
  actions: "200px",
};

function RouteComponent() {
  const { queueGroupId } = Route.useParams();
  const queueGroupAgentsQuery = useQuery(queueGroupAgentsQueryOptions(Number(queueGroupId)));
  const queueGroupAgents = queueGroupAgentsQuery.data;
  const queueGroupQueuesQuery = useQuery(queueGroupQueuesQueryOptions(Number(queueGroupId)));
  const queueGroupQueuesSize = queueGroupQueuesQuery.data?.filter((queue) => queue.active).length ?? 0;
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    form.reset(initiateAgents());
  }, [queueGroupId, queueGroupAgents]);

  useEffect(() => {
    if (!queueGroupId) return;
    void queueGroupAgentsQuery.refetch();
    void queueGroupQueuesQuery.refetch();
  }, [queueGroupId, queueGroupAgentsQuery.refetch, queueGroupQueuesQuery.refetch]);

  const updateQueueGroupAgentsMutation = useMutation({
    mutationFn: updateQueueGroupAttendantsFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queue-group-agents", Number(queueGroupId)] });
      form.reset(initiateAgents());
    },
  });

  const updateQueueGroupAgents = async (agents: CreateQueueAttendant[]) => {
    const updateQueueGroupAgentsPromise = updateQueueGroupAgentsMutation.mutateAsync({
      data: { queueGroupId: Number(queueGroupId), body: agents },
    });
    toast.promise(updateQueueGroupAgentsPromise, {
      loading: "Updating agents...",
      success: "Agents updated successfully",
      error: (error) => error.message || "Failed to update agents. Please try again.",
    });
  };

  const initiateAgents = () => {
    return (
      queueGroupAgents?.map((agent) => ({
        id: agent.id,
        fname: agent.fname,
        lname: agent.lname,
        username: agent.username,
        skill: agent.skill,
        active: agent.active,
        noAssociatedQueues: agent.noAssociatedQueues,
        action: "DO_NOTHING",
      })) ?? []
    );
  };

  const form = useAppForm({
    defaultValues: initiateAgents(),
  });

  function handleSave(e: React.FormEvent, formData: any) {
    e.preventDefault();
    const agentsToUpdate = formData
      .filter((agent: any) => agent.action !== "DO_NOTHING")
      .map((agent) => {
        if (agent.action === "UPDATE" || agent.action === "ADD") {
          return {
            id: agent.id,
            active: true,
            skill: agent.skill,
          };
        } else {
          return {
            id: agent.id,
            skill: agent.skill,
            active: false,
          };
        }
      });
    updateQueueGroupAgents(agentsToUpdate);
  }

  function triggerActionChange(row: any) {
    const actionPath = `${row.index}.action` as any;
    const currentAction = (form.getFieldValue as any)(actionPath);

    if (currentAction === "DO_NOTHING") {
      (form.setFieldValue as any)(actionPath, "UPDATE");
    }
  }

  function handleSkillChange(value: number, field: any, row: any) {
    field.handleChange(value);
    triggerActionChange(row);
    setIsDirty(true);
  }

  function triggerResetRowValues(row: any) {
    const activePath = `${row.index}.active` as any;
    const skillPath = `${row.index}.skill` as any;
    form.setFieldValue(activePath, row.original.active);
    form.setFieldValue(skillPath, row.original.skill);
  }

  function handleActionChange(value: string, field: any, row: any) {
    field.handleChange(value);
    if (value === "DO_NOTHING") {
      triggerResetRowValues(row);
    }
    setIsDirty(true);
  }

  function calculateAssociationStatus(
    noAssociatedQueues: number | undefined
  ): "ALL_ASSOCIATED" | "PARTIAL_ASSOCIATED" | "NO_ASSOCIATED" {
    if (noAssociatedQueues === undefined) {
      return "NO_ASSOCIATED";
    }
    if (noAssociatedQueues === 0) {
      return "NO_ASSOCIATED";
    } else if (noAssociatedQueues < queueGroupQueuesSize) {
      return "PARTIAL_ASSOCIATED";
    } else {
      return "ALL_ASSOCIATED";
    }
  }

  const columns = useMemo<ColumnDef<QueueAttendant & { action: string }>[]>(
    () => [
      {
        accessorKey: "noAssociatedQueues",
        header: "Queues",
        meta: { width: COLUMN_WIDTHS.noAssociatedQueues },
        cell: ({ row }) => (
          <AssociationStatus
            status={calculateAssociationStatus(row.original.noAssociatedQueues)}
            size={12}
            tooltip={`${row.original.noAssociatedQueues} of ${queueGroupQueuesSize} queues associated`}
          />
        ),
      },
      { accessorKey: "username", header: "Username", meta: { width: COLUMN_WIDTHS.username } },
      { accessorKey: "fname", header: "First Name", meta: { width: COLUMN_WIDTHS.fname } },
      { accessorKey: "lname", header: "Last Name", meta: { width: COLUMN_WIDTHS.lname } },

      {
        accessorKey: "skill",
        header: "Skill",
        meta: { width: COLUMN_WIDTHS.skill },
        cell: ({ row }) => (
          <form.Subscribe selector={(s) => (s.values as any[])?.[row.index]}>
            {(currentRow: any) => {
              const currentAction = currentRow?.action ?? "DO_NOTHING";
              const isCurrentlyActive = currentRow?.active ?? row.original.active;
              const isDisabled = !isCurrentlyActive && currentAction !== "ADD";

              return (
                <form.AppField name={`${row.index}.skill` as any}>
                  {(field) => (
                    <Input
                      type="number"
                      min={1}
                      max={18}
                      className="w-20"
                      value={field.state.value ?? 0}
                      disabled={isDisabled}
                      onChange={(e) => !isDisabled && handleSkillChange(Number(e.target.value), field, row)}
                    />
                  )}
                </form.AppField>
              );
            }}
          </form.Subscribe>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        meta: { width: COLUMN_WIDTHS.actions },
        cell: ({ row }) => (
          <form.AppField name={`${row.index}.action` as any}>
            {(field) => (
              <Select
                value={field.state.value ?? "DO_NOTHING"}
                onValueChange={(value) => handleActionChange(value, field, row)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select an action" />
                </SelectTrigger>
                {row.original.active && (
                  <SelectContent>
                    <SelectItem value="DO_NOTHING">Leave as is</SelectItem>
                    <SelectItem value="UPDATE">Update</SelectItem>
                    <SelectItem value="REMOVE">Remove</SelectItem>
                  </SelectContent>
                )}
                {!row.original.active && (
                  <SelectContent>
                    <SelectItem value="DO_NOTHING">Leave as is</SelectItem>
                    <SelectItem value="ADD">Add</SelectItem>
                  </SelectContent>
                )}
              </Select>
            )}
          </form.AppField>
        ),
      },
      {
        accessorKey: "active",
        header: "Active",
        meta: { width: COLUMN_WIDTHS.active },
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.active;
          if (isActive) {
            return <CircleCheckBig className="w-4 h-4 text-blue-400" />;
          } else {
            return null;
          }
        },
      },
    ],
    [queueGroupAgents, queueGroupQueuesSize]
  );

  function resetFormValues() {
    form.reset(initiateAgents());
    setIsDirty(false);
  }

  return (
    <AgentTable
      agents={queueGroupAgents}
      resourceId={Number(queueGroupId)}
      resourceType="queue-group"
      form={form as any}
      columns={columns}
      isDirty={isDirty}
      onSave={handleSave}
      isSaving={updateQueueGroupAgentsMutation.isPending}
      restFormValues={resetFormValues}
    />
  );
}
